/**
 * Consullex Chatbot Widget
 * Widget embeddable pour le site consullex.be
 */

import { ChatWindow } from './ui/ChatWindow'
import { API_CLIENT } from './api-client'
import type { WidgetConfig } from './types'

export class ConsullexChatbot {
  private chatWindow!: ChatWindow
  private config: WidgetConfig
  private isInitialized = false

  constructor(config: Partial<WidgetConfig> & { apiKey?: string } = {}) {
    const siteId = config.siteId || config.apiKey
    if (!siteId) {
      console.error('[ConsullexChatbot] ERREUR: siteId est requis pour utiliser le chatbot')
      throw new Error('siteId est requis')
    }

    this.config = {
      apiUrl: config.apiUrl || 'http://localhost:3001',
      position: config.position || 'bottom-right',
      primaryColor: config.primaryColor || '#1e40af',
      buttonText: config.buttonText || 'Besoin d\'aide ?',
      ...config,
      siteId,
    }

    API_CLIENT.setBaseUrl(this.config.apiUrl)
    API_CLIENT.setSiteId(this.config.siteId)

    // Créer et injecter le widget dans la page
    this.init()
  }

  private init() {
    if (this.isInitialized) return

    // Attendre que le DOM soit prêt
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.createWidget())
    } else {
      this.createWidget()
    }

    this.isInitialized = true
  }

  private createWidget() {
    // Créer le chat window
    this.chatWindow = new ChatWindow(this.config)

    // Injecter les styles
    this.injectStyles()

    // Ajouter le widget au DOM
    document.body.appendChild(this.chatWindow.getElement())
  }

  private injectStyles() {
    const styleId = 'consullex-chatbot-styles'
    
    // Vérifier si les styles n'ont pas déjà été injectés
    if (document.getElementById(styleId)) return

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      /* Consullex Chatbot : charte Consullex 2026, version claire uniquement.
         Un seul bleu (#0000E0) pour l'action, encre #0D0D0D, gris #737373 / #F5F5F5 / #E6E6E6,
         police Figtree, pilules et rayons de 16 px, ni dégradé, ni halo, ni ombre. */
      @import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;700;900&display=swap');

      .consullex-chatbot * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }

      .consullex-chatbot {
        font-family: 'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        position: fixed;
        z-index: 999999;
        color: #0D0D0D;
        -webkit-font-smoothing: antialiased;
      }

      .consullex-chatbot.bottom-right {
        bottom: 24px;
        right: 24px;
      }

      .consullex-chatbot.bottom-left {
        bottom: 24px;
        left: 24px;
      }

      /* ========== LOGO OFFICIEL ========== */
      .consullex-chatbot .consullex-logo {
        font-family: 'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        font-weight: 900;
        letter-spacing: -0.025em;
        line-height: 1;
        white-space: nowrap;
      }

      .consullex-chatbot .consullex-logo-consul {
        color: #0000DF;
      }

      .consullex-chatbot .consullex-logo-lex {
        color: #0D0D0D;
      }

      /* ========== BOUTON FLOTTANT ========== */
      .consullex-chat-button {
        height: 52px;
        padding: 0 24px;
        border-radius: 999px;
        border: 1px solid #0D0D0D;
        background: #FFFFFF;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 17px;
        transition: background-color 0.2s ease;
      }

      .consullex-chat-button:hover {
        background: #F5F5F5;
      }

      .consullex-chat-button:focus-visible {
        outline: 2px solid #0000E0;
        outline-offset: 3px;
      }

      /* ========== FENÊTRE ========== */
      .consullex-chat-window {
        position: absolute;
        bottom: 72px;
        right: 0;
        width: 400px;
        max-width: calc(100vw - 48px);
        height: 620px;
        max-height: calc(100vh - 110px);
        background: #FFFFFF;
        border: 1px solid #E6E6E6;
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        transform: translateY(12px);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease, transform 0.2s ease;
      }

      .consullex-chatbot.bottom-left .consullex-chat-window {
        right: auto;
        left: 0;
      }

      .consullex-chat-window.open {
        transform: translateY(0);
        opacity: 1;
        pointer-events: all;
      }

      /* ========== EN-TÊTE ========== */
      .consullex-chat-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 20px 16px 24px;
        background: #FFFFFF;
        border-bottom: 1px solid #E6E6E6;
      }

      .consullex-chat-header-content {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
      }

      .consullex-chat-header-text h3 {
        font-size: 20px;
      }

      .consullex-chat-header-text p {
        margin-top: 6px;
        font-size: 13px;
        color: #737373;
      }

      .consullex-chat-close {
        width: 40px;
        height: 40px;
        border-radius: 999px;
        border: none;
        background: transparent;
        color: #0D0D0D;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background-color 0.2s ease;
      }

      .consullex-chat-close:hover {
        background: #F5F5F5;
      }

      .consullex-chat-close svg {
        width: 20px;
        height: 20px;
      }

      /* ========== MESSAGES ========== */
      .consullex-chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        background: #FFFFFF;
      }

      .consullex-chat-messages::-webkit-scrollbar {
        width: 6px;
      }

      .consullex-chat-messages::-webkit-scrollbar-thumb {
        background: #E6E6E6;
        border-radius: 999px;
      }

      .consullex-message {
        display: flex;
        gap: 8px;
        animation: consullexMessageIn 0.2s ease;
      }

      @keyframes consullexMessageIn {
        from { opacity: 0; transform: translateY(6px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .consullex-message.user {
        flex-direction: row-reverse;
      }

      /* Plus de pictogramme à côté des messages : le logo suffit dans l'en-tête */
      .consullex-message-avatar {
        display: none;
      }

      .consullex-message-bubble {
        max-width: 82%;
        padding: 12px 16px;
        border-radius: 16px;
        font-size: 15px;
        line-height: 1.55;
        word-wrap: break-word;
        overflow-wrap: anywhere;
      }

      .consullex-message.bot .consullex-message-bubble {
        background: #F5F5F5;
        color: #0D0D0D;
        border-bottom-left-radius: 4px;
      }

      .consullex-message.user .consullex-message-bubble {
        background: #0000E0;
        color: #FFFFFF;
        border-bottom-right-radius: 4px;
      }

      /* ========== RÉPONSES RAPIDES ========== */
      .consullex-quick-replies {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .consullex-quick-reply {
        padding: 9px 16px;
        border-radius: 999px;
        border: 1px solid #0D0D0D;
        background: #FFFFFF;
        color: #0D0D0D;
        font-family: inherit;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease;
      }

      .consullex-quick-reply:hover {
        background: #0D0D0D;
        color: #FFFFFF;
      }

      /* ========== EN TRAIN D'ÉCRIRE ========== */
      .consullex-typing {
        display: flex;
        gap: 4px;
        padding: 4px 2px;
      }

      .consullex-typing-dot {
        width: 7px;
        height: 7px;
        border-radius: 999px;
        background: #737373;
        animation: consullexTyping 1.2s ease-in-out infinite;
      }

      .consullex-typing-dot:nth-child(2) {
        animation-delay: 0.2s;
      }

      .consullex-typing-dot:nth-child(3) {
        animation-delay: 0.4s;
      }

      @keyframes consullexTyping {
        0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
        30% { opacity: 1; transform: translateY(-3px); }
      }

      /* ========== ZONE DE SAISIE ========== */
      .consullex-chat-input {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        padding: 12px 16px;
        background: #FFFFFF;
        border-top: 1px solid #E6E6E6;
      }

      .consullex-chat-input-field {
        flex: 1;
        min-height: 44px;
        max-height: 100px;
        padding: 11px 18px;
        border: 1px solid #E6E6E6;
        border-radius: 22px;
        background: #FFFFFF;
        color: #0D0D0D;
        font-family: inherit;
        font-size: 15px;
        line-height: 1.4;
        resize: none;
        outline: none;
        transition: border-color 0.2s ease;
      }

      .consullex-chat-input-field::placeholder {
        color: #737373;
      }

      .consullex-chat-input-field:focus {
        border-color: #0000E0;
      }

      .consullex-chat-send {
        width: 44px;
        height: 44px;
        border-radius: 999px;
        border: none;
        background: #0000E0;
        color: #FFFFFF;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background-color 0.2s ease;
      }

      .consullex-chat-send:hover {
        background: #0000C4;
      }

      .consullex-chat-send:disabled {
        background: #E6E6E6;
        color: #737373;
        cursor: not-allowed;
      }

      .consullex-chat-send svg {
        width: 18px;
        height: 18px;
      }

      /* ========== FORMULAIRE DE CONTACT ========== */
      .consullex-contact-form {
        background: #F5F5F5;
        border-radius: 16px;
        padding: 20px;
      }

      .consullex-form-header {
        margin-bottom: 16px;
      }

      .consullex-form-header h4 {
        font-size: 17px;
        font-weight: 700;
        color: #0D0D0D;
        letter-spacing: -0.025em;
      }

      .consullex-form-header p {
        margin-top: 4px;
        font-size: 13px;
        color: #737373;
      }

      .consullex-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .consullex-form-row {
        display: flex;
        gap: 10px;
      }

      .consullex-form-row .consullex-form-group {
        flex: 1;
        min-width: 0;
      }

      .consullex-form-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .consullex-form-group label {
        font-size: 13px;
        font-weight: 500;
        color: #404040;
      }

      .consullex-form-group input {
        height: 44px;
        padding: 0 16px;
        border: 1px solid #E6E6E6;
        border-radius: 999px;
        background: #FFFFFF;
        color: #0D0D0D;
        font-family: inherit;
        font-size: 15px;
        outline: none;
        transition: border-color 0.2s ease;
        width: 100%;
      }

      .consullex-form-group input::placeholder {
        color: #A3A3A3;
      }

      .consullex-form-group input:focus {
        border-color: #0000E0;
      }

      .consullex-form-submit {
        margin-top: 4px;
        height: 48px;
        border-radius: 999px;
        border: none;
        background: #0000E0;
        color: #FFFFFF;
        font-family: inherit;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      .consullex-form-submit:hover {
        background: #0000C4;
      }

      .consullex-form-submit:disabled {
        background: #E6E6E6;
        color: #737373;
        cursor: not-allowed;
      }

      .consullex-form-error {
        margin-top: 8px;
        padding: 10px 14px;
        border-radius: 12px;
        background: #FEF2F2;
        color: #EF4444;
        font-size: 13px;
      }

      /* ========== CALENDLY ========== */
      .consullex-calendly-wrapper {
        margin: 12px 0;
        border-radius: 16px;
        overflow: hidden;
        border: 1px solid #E6E6E6;
        background: #FFFFFF;
      }

      /* ========== PIED ========== */
      .consullex-footer-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px 16px;
        background: #FFFFFF;
        border-top: 1px solid #E6E6E6;
      }

      .consullex-powered-by {
        font-size: 11px;
        color: #999999;
      }

      .consullex-powered-by a {
        color: #737373;
        text-decoration: none;
      }

      .consullex-powered-by a:hover {
        color: #0D0D0D;
      }

      /* ========== MOBILE ========== */
      @media (max-width: 480px) {
        .consullex-chatbot.bottom-right,
        .consullex-chatbot.bottom-left {
          bottom: 16px;
          right: 16px;
          left: auto;
        }

        .consullex-chat-button {
          height: 48px;
          padding: 0 20px;
          font-size: 16px;
        }

        .consullex-chat-window {
          position: fixed;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          max-width: 100vw;
          max-height: 100vh;
          max-height: 100dvh;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          border-radius: 0;
          border: none;
        }

        .consullex-chat-messages {
          padding: 16px;
        }

        .consullex-message-bubble {
          max-width: 88%;
        }

        .consullex-form-row {
          flex-direction: column;
        }
      }
    `

    document.head.appendChild(style)
  }

  // API publique
  public open() {
    this.chatWindow?.open()
  }

  public close() {
    this.chatWindow?.close()
  }

  public destroy() {
    this.chatWindow?.destroy()
    document.getElementById('consullex-chatbot-styles')?.remove()
  }
}

